/**
 * Sentry PII hardening (compliance — Inverio handles founder/investor PII and Sentry is a
 * US-incorporated processor). A `beforeSend` scrubber strips direct identifiers and
 * redacts emails/tokens from anything that would otherwise leave the device, plus an
 * `ignoreErrors` list so alerts fire on real problems, not non-actionable noise.
 *
 * Pure + unit-tested (src/lib/__tests__/sentry.test.ts) — no Sentry import here.
 */

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
// JWTs, OpenAI-style keys, Bearer tokens, and long hex/secret blobs.
const TOKEN_RE =
  /\b(eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+|sk-[A-Za-z0-9]{16,}|Bearer\s+[A-Za-z0-9._-]{12,}|[A-Fa-f0-9]{32,})\b/g;

/** Redact emails + token-like strings from free text before it leaves the device. */
export function redactPII(input: string): string {
  return input.replace(EMAIL_RE, '[email]').replace(TOKEN_RE, '[redacted]');
}

/** The subset of a Sentry event we touch — all optional, so the real Event satisfies it. */
export interface ScrubbableEvent {
  message?: string;
  server_name?: string;
  user?: { id?: string | number; email?: string; username?: string; ip_address?: string | null; [k: string]: unknown } | null;
  request?: {
    url?: string;
    query_string?: unknown;
    cookies?: unknown;
    headers?: Record<string, string>;
    data?: unknown;
  };
  breadcrumbs?: { message?: string; data?: unknown }[] | null;
  exception?: { values?: { value?: string; stacktrace?: unknown }[] };
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  tags?: Record<string, unknown>;
}

/**
 * Stack-frame fields that identify a CODE LOCATION, never a person.
 *
 * These are exempt from redaction because Sentry matches a frame to an uploaded
 * source map by its `filename` / `abs_path`, and an Expo bundle is named
 * `index-<32 hex>.js` — which `TOKEN_RE`'s long-hex rule reads as a secret. Every
 * web frame was therefore shipping as `index-[redacted].js`, so no artifact could
 * ever match and symbolication was permanently impossible. (Found 2026-08-08:
 * every web issue in Sentry had minified frames and an unusable title.)
 *
 * Exempting the location does NOT weaken the scrubber: the frame's other fields
 * (`vars`, `context_line`, …) are still redacted, as is everything around it.
 */
const CODE_LOCATION_KEYS = new Set(['filename', 'abs_path', 'module']);

/**
 * Depth-capped recursive redaction of every string value inside a container (objects +
 * arrays), mutating in place. Closes the "allowlist missed a field" gap — covers
 * breadcrumb `data`, request bodies, `extra`/`contexts`/`tags`, and stack-frame strings.
 */
function redactDeep(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined || depth > 8) return value;
  if (typeof value === 'string') return redactPII(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = redactDeep(value[i], depth + 1);
    return value;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    for (const k of Object.keys(o)) {
      if (CODE_LOCATION_KEYS.has(k) && typeof o[k] === 'string') continue;
      o[k] = redactDeep(o[k], depth + 1);
    }
    return value;
  }
  return value;
}

/**
 * Strip/redact PII from an outgoing Sentry event. Keeps `user.id` (a uuid, needed to
 * correlate) but removes email/username/ip; drops device name + cookies + auth headers;
 * redacts emails/tokens in the message, exception values, breadcrumbs, and request url.
 */
export function scrubSentryEvent<T extends ScrubbableEvent>(event: T): T {
  if (!event) return event;

  // 1) Drop direct identifiers entirely (not just redact).
  if ('server_name' in event) delete event.server_name; // often "Jane's iPhone"
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
    const id = event.user.id;
    redactDeep(event.user); // scrub any other custom PII on the user…
    if (id !== undefined) event.user.id = id; // …but keep the (uuid) correlation id
  }
  if (event.request) {
    delete event.request.cookies;
    if (event.request.headers) {
      for (const h of Object.keys(event.request.headers)) {
        if (/^(authorization|cookie|x-api-key|x-auth-token)$/i.test(h)) delete event.request.headers[h];
      }
    }
  }

  // 2) Recursively redact emails/tokens from every PII-bearing container — covers the
  //    fields a field-by-field allowlist misses: breadcrumb `data`, request body/url/query,
  //    `extra`, `contexts`, `tags`, exception values + stack frames.
  if (typeof event.message === 'string') event.message = redactPII(event.message);
  redactDeep(event.breadcrumbs);
  redactDeep(event.exception);
  redactDeep(event.request);
  redactDeep(event.extra);
  redactDeep(event.contexts);
  redactDeep(event.tags);

  return event;
}

/**
 * Non-actionable errors to drop entirely (so the new alert rules fire on real problems).
 * Network/timeout/cancel noise; most are already kept out of Sentry by the centralized
 * error handler, this is belt-and-suspenders for unhandled rejections + native errors.
 */
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
  'Network request failed',
  'AbortError',
  'TypeError: Failed to fetch',
  'Load failed',
  'Non-Error promise rejection captured',
  /request (was )?(aborted|cancelled)/i,
];
